import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetPalletBaseTypes } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { IPalletBaseType, PalletBaseType } from '../../../@types/model/masterData/palletBaseType/palletBaseType';
import PalletBaseTypeHttpService from '../../../services/http/masterData/palletBaseTypeHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { PalletBaseTypeFormValues, IPalletBaseTypeFormValues } from '../../../@types/model/masterData/palletBaseType/palletBaseTypeFormValues';
import PalletBaseTypeForm from './form/PalletBaseTypeForm';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setPalletBaseTypeMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const PalletBaseTypeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const palletBaseTypes = useAppSelector(x => x.masterData.palletBaseTypes);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedPalletBaseType, setSelectedPalletBaseType] = useState<IPalletBaseType>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingPalletBaseType, setDeletingPalletBaseType] = useState<IPalletBaseType>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await PalletBaseTypeHttpService.getPalletBaseTypeData();
                    dispatch(dataSetPalletBaseTypes(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading pallet base types.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedPalletBaseType(undefined);
        setFormGuid(undefined);
    }, []);

    const editPalletBaseType = useCallback((palletBaseType : IPalletBaseType) => {
        setSelectedPalletBaseType(palletBaseType);
        setFormGuid(palletBaseType.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IPalletBaseTypeFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await PalletBaseTypeHttpService.addOrUpdatePalletBaseType(PalletBaseType(payload));
            const updatedList = upsertArrayElement(palletBaseTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPalletBaseTypes(updatedList));
            await setPalletBaseTypeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPalletBaseType ? 'Pallet base type updated successfully.' : 'Pallet base type added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating pallet base type data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((palletBaseType : IPalletBaseType) => {
        setDeletingPalletBaseType(palletBaseType);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingPalletBaseType(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removePalletBaseType = async () => {
        if (!deletingPalletBaseType) return;
        const updatedPalletBaseType = { ...deletingPalletBaseType, isActive: false };
        setIsLoading(true);
        try {
            const res = await PalletBaseTypeHttpService.deletePalletBaseType(updatedPalletBaseType.id);
            const updatedList = upsertArrayElement(palletBaseTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPalletBaseTypes(updatedList));
            await setPalletBaseTypeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Pallet base type deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting pallet base type.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return PalletBaseTypeFormValues(selectedPalletBaseType);
        }
    }, [selectedPalletBaseType, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Pallet Base Type'
                    isEdit={!!selectedPalletBaseType}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <PalletBaseTypeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IPalletBaseType>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editPalletBaseType}
                        enableEditing={hasEditingRight}
                        enableDeleting={palletBaseType => palletBaseType.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Default Weight (kg)', field: 'defaultWeight', type: 'numeric', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={palletBaseTypes}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                        fitWidthToPage
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Pallet Base Type'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this pallet base type?'
                onAccept={removePalletBaseType}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PalletBaseTypeScreen;
